/* eslint-disable no-console */
/* eslint-disable unicorn/prefer-module */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { getAniosCierreDisponibles, getAniosFacturaDisponibles } from "../../constants/anios";
import { BROKER_ACTIVACION } from "../../constants/permisos";
import { ROLES } from "../../constants/roles";
import { useAuthContext } from "../../context/auth";
import regionLogo from "../../img/region-logo.png";
import { getContratos, getOportunidades, getUsers, getClientes, getEdificios, getIntermediarios } from "../../utils";
import {
  calcularPublicidadMensual,
  calcularBTLMensual,
  calcularHardwareMensual,
  calcularGestionContenidosMensual,
  normalizeMonth,
  convertirAMonedaLocal,
} from "../../utils/calculateVentas";
import { getClientesPorBroker } from "../../utils/cotizacionesGuardadas";
import { loadExchangeRates } from "../../utils/exchangeRateService";
import { getImporteFacturable } from "../../utils/getImporteFacturable";
import { matchGrupoOpp } from "../../utils/grupoBroker";
import { resolverNombreEjecutivo } from "../../utils/resolverNombreEjecutivo";
import { client } from "../../utils/supabase";
import { useExchangeRates } from "../../utils/useExchangeRates";

import { ChartModal } from "./components/ChartModal";
import { DetailsModal } from "./components/DetailsModal";
import { PipelineModal } from "./components/PipelineModal";
import { VentasTable } from "./components/VentasTable";
import { VPHeader } from "./components/VPHeader";
import { FACTOR_90, FACTOR_75 } from "./constants";
import { useVPAggregation } from "./hooks/useVPAggregation";
import { useVPCierresCalcs } from "./hooks/useVPCierresCalcs";
import { useVPFacturacionCalcs } from "./hooks/useVPFacturacionCalcs";
import { useVPFormatting } from "./hooks/useVPFormatting";
import { useVPPipelineCalcs } from "./hooks/useVPPipelineCalcs";
import styles from "./components/stylesVentasTabla.module.scss";

import "./stylesVentasProyeccion.scss";

const VentasProyeccion = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonthIndex = currentDate.getMonth();

  // UI State
  // Año que muestra toda la vista (tabla, totales, gráfico). Default = año en curso.
  const [anoSeleccionado, setAnoSeleccionado] = useState(currentYear);
  const anoAA = anoSeleccionado - 1; // año anterior (comparativo)
  const anoAAA = anoSeleccionado - 2; // año anterior anterior (comparativo facturación)
  const [showInDollars, setShowInDollars] = useState(false);
  const [mostrar2025, setMostrar2025] = useState(false);
  const [vistaCompleta, setVistaCompleta] = useState("cierres");
  const [verChileYPeru, setVerChileYPeru] = useState(false);
  const [verYTD, setVerYTD] = useState(false);
  // Filtro de grupo Todos/Visiona/AdLab (Perú-only). "" = Todos.
  const [filtroGrupo, setFiltroGrupo] = useState("");

  // Data State - país actual
  const [oportunidades, setOportunidades] = useState([]);
  const [salesBTL, setSalesBTL] = useState([]);
  const [negociosCarteleria, setNegociosCarteleria] = useState([]);
  const [costosGestionData, setCostosGestionData] = useState([]);
  const [contratosData, setContratosData] = useState([]);

  // Data State - otro país (modo combinado)
  const [oportunidadesOtroPais, setOportunidadesOtroPais] = useState([]);
  const [salesBTLOtroPais, setSalesBTLOtroPais] = useState([]);
  const [negociosCarteleriaOtroPais, setNegociosCarteleriaOtroPais] = useState([]);
  const [costosGestionDataOtroPais, setCostosGestionDataOtroPais] = useState([]);
  const [contratosDataOtroPais, setContratosDataOtroPais] = useState([]);
  const [datosOtroPaisCargados, setDatosOtroPaisCargados] = useState(false);

  // Modal State
  const [modal, setModal] = useState({ open: false, title: "", items: [] });
  const [pipelineModal, setPipelineModal] = useState({ open: false, title: "", items: [], total: 0 });
  const [pipelineSort, setPipelineSort] = useState("importe");
  const [chartModalOpen, setChartModalOpen] = useState(false);

  // Lookup data
  const [usuarios, setUsuarios] = useState([]);
  const [intermediarios, setIntermediarios] = useState([]);
  const [clientes, setClientes] = useState({});
  const [edificios, setEdificios] = useState({});
  const [loading, setLoading] = useState(true);

  // Calculated State - país actual. Mapas keyed por año (anoSeleccionado + comparativos).
  const [cierresPorAno, setCierresPorAno] = useState({});
  const [facturacionPorAno, setFacturacionPorAno] = useState({});

  // Calculated State - otro país (modo combinado)
  const [cierresPorAnoOtro, setCierresPorAnoOtro] = useState({});
  const [facturacionPorAnoOtro, setFacturacionPorAnoOtro] = useState({});

  const { pais, rol, agencia } = useAuthContext() || {};
  const modoCombinado = verChileYPeru && datosOtroPaisCargados;

  // Años seleccionables = años con datos (cierre + factura) ∪ año en curso (siempre).
  const aniosDisponibles = useMemo(() => {
    const cierre = getAniosCierreDisponibles([...oportunidades, ...salesBTL], contratosData);
    const factura = getAniosFacturaDisponibles([...oportunidades, ...salesBTL, ...contratosData]);
    const set = new Set([...cierre, ...factura, String(currentYear)]);
    return [...set].map(Number).sort((a, b) => b - a);
  }, [oportunidades, salesBTL, contratosData, currentYear]);

  // === HOOKS ===

  const { dolarMensual, dolarMensualOtroPais, tipoCambioActual, dolarFallback } = useExchangeRates(pais);
  const otroPaisNombre = pais === "Chile" ? "Perú" : "Chile";
  const tipoCambioOtroPais = dolarFallback[otroPaisNombre] || 0;

  const { localString, moneda, getMoneda, getDisplayValue, getMonthName, calcularVariacion, shouldShowVsAA } =
    useVPFormatting(pais, showInDollars, modoCombinado);

  // === FILTRO DE GRUPO (Perú-only) — fuente única para hooks y modales ===
  // Filtra los arrays del PAÍS por matchGrupoOpp antes de alimentar los cálculos. Chile
  // (opp.pais !== "Perú") NUNCA se filtra (modo combinado intacto). filtroGrupo="" → pass-through.
  // esBTL=true solo para salesBTL (pauta y contratos van esBTL=false, período-aware).
  const oportunidadesGrupo = useMemo(
    () => oportunidades.filter((o) => o.pais !== "Perú" || matchGrupoOpp(filtroGrupo, o, clientes[o.client_id], false)),
    [oportunidades, clientes, filtroGrupo]
  );
  const salesBTLGrupo = useMemo(
    () => salesBTL.filter((b) => b.pais !== "Perú" || matchGrupoOpp(filtroGrupo, b, clientes[b.client_id], true)),
    [salesBTL, clientes, filtroGrupo]
  );
  const contratosGrupo = useMemo(
    () => contratosData.filter((c) => c.pais !== "Perú" || matchGrupoOpp(filtroGrupo, c, clientes[c.client_id], false)),
    [contratosData, clientes, filtroGrupo]
  );
  // Cartelería/HW es país-level (sin client_id, no es negocio del broker): bajo AdLab se excluye
  // entera → Todos = Visiona + AdLab cierra y AdLab no cuela cartelería de la casa. Todos/Visiona: as-is.
  const negociosCarteleriaGrupo = useMemo(
    () => (filtroGrupo === "grupo:adlab" ? [] : negociosCarteleria),
    [filtroGrupo, negociosCarteleria]
  );

  const {
    calcularCierresPub,
    calcularCierresBTL,
    calcularCierresHW,
    calcularCierresCart,
    calcularCierresContratos,
    calcularCierresPubOtro,
    calcularCierresBTLOtro,
    calcularCierresHWOtro,
    calcularCierresCartOtro,
    calcularCierresContratosOtro,
  } = useVPCierresCalcs({
    oportunidades: oportunidadesGrupo,
    salesBTL: salesBTLGrupo,
    negociosCarteleria: negociosCarteleriaGrupo,
    contratosData: contratosGrupo,
    oportunidadesOtroPais,
    salesBTLOtroPais,
    negociosCarteleriaOtroPais,
    contratosDataOtroPais,
    pais,
  });

  const {
    calcularFacturacionPubProyeccion,
    calcularFacturacionBTLProyeccion,
    calcularFacturacionContratosProyeccion,
    calcularFacturacionPubProyeccionOtro,
    calcularFacturacionBTLProyeccionOtro,
    calcularFacturacionContratosProyeccionOtro,
  } = useVPFacturacionCalcs({
    oportunidades: oportunidadesGrupo,
    salesBTL: salesBTLGrupo,
    contratosData: contratosGrupo,
    oportunidadesOtroPais,
    salesBTLOtroPais,
    contratosDataOtroPais,
    pais,
  });

  const { getDesglose } = useVPAggregation({
    cierresPorAno,
    facturacionPorAno,
    cierresPorAnoOtro,
    facturacionPorAnoOtro,
    showInDollars,
    modoCombinado,
    dolarMensual,
    dolarMensualOtroPais,
    tipoCambioActual,
    tipoCambioOtroPais,
    pais,
  });

  const {
    calcularPipeline,
    calcularPipelineBruto,
    calcularPipelineBrutoTotal,
    calcularPipelineFacturacion,
    getPipelineItems,
  } = useVPPipelineCalcs({
    oportunidades: oportunidadesGrupo,
    oportunidadesOtroPais,
    contratosData: contratosGrupo,
    contratosOtroPais: contratosDataOtroPais,
    showInDollars,
    modoCombinado,
    tipoCambioActual,
    tipoCambioOtroPais,
    pais,
    anoSeleccionado,
  });

  // === DATA LOADING ===

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Garantiza que tipos de cambio (UF/USD) esten cargados antes de cualquier
        // calculo que convierta monedas. Evita race condition donde los calculos
        // corren con cache vacio y convierten USD→CLP como 0.
        await loadExchangeRates();
        const proyResults = await Promise.all([
          getOportunidades({ pais }),
          getUsers(),
          client.from("negocios_carteleria").select("*").eq("pais", pais).neq("eliminada", true),
          client.from("sales_btl").select("*").eq("pais", pais).eq("eliminada", false),
          client.from("costos_gestion_contenidos").select("*").eq("pais", pais),
          getClientes({ pais }),
          getEdificios({ pais }),
          getContratos({ pais }),
          getIntermediarios({ pais }),
        ]);
        const usuariosData = proyResults[1];
        const intermediariosData = proyResults[8] || [];
        const negociosData = proyResults[2];
        const costosGestionRaw = proyResults[4];
        const edificiosData = proyResults[6];
        // Rol BROKER sin scoping de LECTURA en su país: ve toda la proyección de Perú
        // (como comercial/gerencia). El modo combinado (otro país) SÍ sigue scopeado abajo
        // → el broker no ve Chile. La edición/creación sigue acotada a AdLab en otros guards.
        const allOportunidades = proyResults[0];
        const clientesData = proyResults[5];
        const salesBTLData = proyResults[3];
        const contratosRaw = proyResults[7];

        setUsuarios(usuariosData || []);
        setIntermediarios(intermediariosData);
        setNegociosCarteleria(negociosData.data || []);
        setCostosGestionData(costosGestionRaw.data || []);

        const clientesMap = {};
        (clientesData || []).forEach((c) => {
          clientesMap[c.id] = c;
        });
        setClientes(clientesMap);

        const edificiosMap = {};
        (edificiosData || []).forEach((e) => {
          edificiosMap[e.id] = e;
        });
        setEdificios(edificiosMap);

        const filteredOportunidades = allOportunidades
          .filter((o) => o.pais === pais && !o.eliminada)
          .map((o) => ({
            ...o,
            mes_cierre: normalizeMonth(o.mes_cierre, o.created_at, o.ano),
            mes_factura: normalizeMonth(o.mes_factura, o.created_at, o.ano),
          }));
        setOportunidades(filteredOportunidades);

        const filteredBTL = (salesBTLData.data || [])
          .filter((btl) => btl.pais === pais && !btl.eliminada)
          .map((btl) => ({
            ...btl,
            mes_cierre: normalizeMonth(btl.mes_cierre, btl.created_at, btl.ano),
            mes_factura: normalizeMonth(btl.mes_factura, btl.created_at, btl.ano),
          }));
        setSalesBTL(filteredBTL);

        const filteredContratos = (contratosRaw || []).filter((c) => c.pais === pais && !c.eliminada);
        setContratosData(filteredContratos);
      } catch (error) {
        console.error("Error cargando datos:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [pais, rol, agencia]);

  // Cargar datos del OTRO país cuando se activa modo combinado
  useEffect(() => {
    if (!verChileYPeru || datosOtroPaisCargados) return;

    const otroPais = pais === "Chile" ? "Perú" : "Chile";

    const fetchDatosOtroPais = async () => {
      try {
        const otroResults = await Promise.all([
          getOportunidades({ pais: otroPais }),
          client.from("negocios_carteleria").select("*").eq("pais", otroPais).neq("eliminada", true),
          client.from("sales_btl").select("*").eq("pais", otroPais).eq("eliminada", false),
          client.from("costos_gestion_contenidos").select("*").eq("pais", otroPais),
          getContratos({ pais: otroPais }),
        ]);
        const negociosOtro = otroResults[1];
        const costosGestionOtro = otroResults[3];
        let oportunidadesOtro = otroResults[0];
        let salesBTLOtro = otroResults[2];
        let contratosOtro = otroResults[4];

        // === SCOPING BROKER en modo combinado (otro país) — mismo criterio, sin leak ===
        if (rol === ROLES.BROKER) {
          let carteraOtroIds = new Set();
          try {
            const carteraOtro = await getClientesPorBroker(otroPais, agencia);
            carteraOtroIds = new Set((carteraOtro || []).map((c) => Number(c.id)));
          } catch {
            carteraOtroIds = new Set();
          }
          const enCarteraOtro = (cid) => carteraOtroIds.has(Number(cid));
          oportunidadesOtro = (oportunidadesOtro || []).filter((o) => enCarteraOtro(o.client_id));
          salesBTLOtro = {
            ...salesBTLOtro,
            data: (salesBTLOtro?.data || []).filter((b) => enCarteraOtro(b.client_id)),
          };
          contratosOtro = (contratosOtro || []).filter((c) => enCarteraOtro(c.client_id));

          // Filtro temporal broker (otro país): mismo criterio que el bloque principal.
          const brokerDesdeOtro = (o) => !o.fecha_cierre || o.fecha_cierre >= BROKER_ACTIVACION;
          oportunidadesOtro = oportunidadesOtro.filter(brokerDesdeOtro);
          contratosOtro = contratosOtro.filter(brokerDesdeOtro);
          salesBTLOtro = { ...salesBTLOtro, data: (salesBTLOtro?.data || []).filter(brokerDesdeOtro) };
        }

        const filteredOportunidadesOtro = oportunidadesOtro
          .filter((o) => o.pais === otroPais && !o.eliminada)
          .map((o) => ({
            ...o,
            mes_cierre: normalizeMonth(o.mes_cierre, o.created_at, o.ano),
            mes_factura: normalizeMonth(o.mes_factura, o.created_at, o.ano),
          }));
        setOportunidadesOtroPais(filteredOportunidadesOtro);

        setNegociosCarteleriaOtroPais(negociosOtro.data || []);
        setCostosGestionDataOtroPais(costosGestionOtro.data || []);

        const filteredBTLOtro = (salesBTLOtro.data || [])
          .filter((btl) => btl.pais === otroPais && !btl.eliminada)
          .map((btl) => ({
            ...btl,
            mes_cierre: normalizeMonth(btl.mes_cierre, btl.created_at, btl.ano),
            mes_factura: normalizeMonth(btl.mes_factura, btl.created_at, btl.ano),
          }));
        setSalesBTLOtroPais(filteredBTLOtro);

        const filteredContratosOtro = (contratosOtro || []).filter((c) => c.pais === otroPais && !c.eliminada);
        setContratosDataOtroPais(filteredContratosOtro);

        setDatosOtroPaisCargados(true);
      } catch (error) {
        console.error("Error cargando datos del otro país:", error);
      }
    };

    fetchDatosOtroPais();
  }, [verChileYPeru, datosOtroPaisCargados, pais, rol, agencia]);

  // === CALCULATION ORCHESTRATION ===

  useEffect(() => {
    if (loading) return;

    const calcularTodo = async () => {
      try {
        // Re-asegura cache de tipos de cambio antes de calcular
        // (defensivo: si fetchData termino pero el cache se invalido).
        await loadExchangeRates();
        // Helper: suma element-wise de dos arrays de 12
        const sumarArrays = (a, b) => a.map((v, i) => v + (b[i] || 0));

        // CIERRES (contratos sumados en publicidad) — año seleccionado + AA
        const buildCierres = (ano) => ({
          publicidad: sumarArrays(calcularCierresPub(ano), calcularCierresContratos(ano)),
          hardware: calcularCierresHW(ano),
          carteleria: calcularCierresCart(ano),
          btl: calcularCierresBTL(ano),
        });
        setCierresPorAno({ [anoSeleccionado]: buildCierres(anoSeleccionado), [anoAA]: buildCierres(anoAA) });

        // FACTURACIÓN comparativo AAA: solo facturafecha real (sin proyección)
        const buildFacturacionReal = async (ano) => {
          const pub = await calcularPublicidadMensual(ano, oportunidadesGrupo, pais);
          const cont = calcularFacturacionContratosProyeccion(ano);
          const btl = await calcularBTLMensual(ano, salesBTLGrupo, pais);
          const hw = await calcularHardwareMensual(ano, negociosCarteleriaGrupo, pais, costosGestionData);
          const cart = await calcularGestionContenidosMensual(ano, negociosCarteleriaGrupo, pais, costosGestionData);
          return { publicidad: sumarArrays(pub, cont), hardware: hw, carteleria: cart, btl };
        };

        // FACTURACIÓN con proyección (facturafecha + mes_factura) — año seleccionado + AA
        const buildFacturacionProy = async (ano) => {
          const pub = sumarArrays(calcularFacturacionPubProyeccion(ano), calcularFacturacionContratosProyeccion(ano));
          const btl = calcularFacturacionBTLProyeccion(ano);
          const hw = await calcularHardwareMensual(ano, negociosCarteleriaGrupo, pais, costosGestionData);
          const cart = await calcularGestionContenidosMensual(ano, negociosCarteleriaGrupo, pais, costosGestionData);
          return { publicidad: pub, hardware: hw, carteleria: cart, btl };
        };

        setFacturacionPorAno({
          [anoAAA]: await buildFacturacionReal(anoAAA),
          [anoAA]: await buildFacturacionProy(anoAA),
          [anoSeleccionado]: await buildFacturacionProy(anoSeleccionado),
        });
      } catch (error) {
        console.error("Error calculando:", error);
      }
    };

    calcularTodo();
  }, [
    loading,
    oportunidadesGrupo,
    salesBTLGrupo,
    negociosCarteleriaGrupo,
    costosGestionData,
    contratosGrupo,
    pais,
    anoSeleccionado,
    anoAA,
    anoAAA,
  ]);

  // CÁLCULOS OTRO PAÍS (modo combinado)
  useEffect(() => {
    if (!modoCombinado || oportunidadesOtroPais.length === 0) return;

    const otroPais = pais === "Chile" ? "Perú" : "Chile";

    const calcularTodoOtroPais = async () => {
      try {
        const sumarArrays = (a, b) => a.map((v, i) => v + (b[i] || 0));
        const negocios = negociosCarteleriaOtroPais;
        const costos = costosGestionDataOtroPais;

        // CIERRES otro país — año seleccionado + AA
        const buildCierresOtro = (ano) => ({
          publicidad: sumarArrays(calcularCierresPubOtro(ano), calcularCierresContratosOtro(ano)),
          hardware: calcularCierresHWOtro(ano),
          carteleria: calcularCierresCartOtro(ano),
          btl: calcularCierresBTLOtro(ano),
        });
        setCierresPorAnoOtro({
          [anoSeleccionado]: buildCierresOtro(anoSeleccionado),
          [anoAA]: buildCierresOtro(anoAA),
        });

        // FACTURACIÓN comparativo AAA otro país: solo facturafecha real
        const buildFacturacionRealOtro = async (ano) => {
          const pub = await calcularPublicidadMensual(ano, oportunidadesOtroPais, otroPais);
          const cont = calcularFacturacionContratosProyeccionOtro(ano);
          const btl = await calcularBTLMensual(ano, salesBTLOtroPais, otroPais);
          const hw = await calcularHardwareMensual(ano, negocios, otroPais, costos);
          const cart = await calcularGestionContenidosMensual(ano, negocios, otroPais, costos);
          return { publicidad: sumarArrays(pub, cont), hardware: hw, carteleria: cart, btl };
        };

        // FACTURACIÓN con proyección otro país — año seleccionado + AA
        const buildFacturacionProyOtro = async (ano) => {
          const pub = sumarArrays(
            calcularFacturacionPubProyeccionOtro(ano),
            calcularFacturacionContratosProyeccionOtro(ano)
          );
          const btl = calcularFacturacionBTLProyeccionOtro(ano);
          const hw = await calcularHardwareMensual(ano, negocios, otroPais, costos);
          const cart = await calcularGestionContenidosMensual(ano, negocios, otroPais, costos);
          return { publicidad: pub, hardware: hw, carteleria: cart, btl };
        };

        setFacturacionPorAnoOtro({
          [anoAAA]: await buildFacturacionRealOtro(anoAAA),
          [anoAA]: await buildFacturacionProyOtro(anoAA),
          [anoSeleccionado]: await buildFacturacionProyOtro(anoSeleccionado),
        });
      } catch (error) {
        console.error("Error calculando otro país:", error);
      }
    };

    calcularTodoOtroPais();
  }, [
    modoCombinado,
    oportunidadesOtroPais,
    salesBTLOtroPais,
    negociosCarteleriaOtroPais,
    costosGestionDataOtroPais,
    contratosDataOtroPais,
    pais,
    anoSeleccionado,
    anoAA,
    anoAAA,
  ]);

  // === MODAL HANDLERS ===

  const handleOpenModal = useCallback(
    (monthIndex, tipo, year, vertical) => {
      const ano = Number(year || currentYear);
      const start = monthIndex + 1;
      const end = monthIndex + 1;
      const isCierres = tipo === "cierres";
      const campoMes = isCierres ? "mes_cierre" : "mes_factura";

      let todas = [];

      if (vertical === "publicidad" || vertical === null) {
        todas = [
          ...todas,
          ...oportunidadesGrupo
            .filter((o) => {
              if (o.estatus !== "100%" || o.eliminada) return false;
              // Misma lógica de prioridad que useVPFacturacionCalcs:
              // facturafecha (real) > mes_factura (proyección) para facturación
              let mesNum = -1;
              let anoNum = -1;
              if (!isCierres && o.facturafecha) {
                const d = new Date(o.facturafecha);
                if (!isNaN(d.getTime())) {
                  mesNum = d.getMonth() + 1;
                  anoNum = d.getFullYear();
                }
              }
              if (mesNum < 0 && o[campoMes]) {
                const [mesStr, anoStr] = o[campoMes].split("-");
                mesNum = Number(mesStr);
                anoNum = Number(anoStr);
              }
              return anoNum === ano && mesNum >= start && mesNum <= end;
            })
            .map((o) => {
              // Ejecutivo = dueño de la cuenta (cliente.ejecutivo), fallback al de la venta.
              // Si es broker, se muestra el nombre del intermediario (ej. "AdLab").
              const ejecutivoId = clientes[o.client_id]?.ejecutivo ?? o.ejecutivo;
              const ejecutivoNombre =
                resolverNombreEjecutivo(
                  ejecutivoId,
                  usuarios,
                  intermediarios,
                  o.client_id,
                  clientes,
                  o.created_at,
                  o.estatus,
                  o.fecha_cierre,
                  o.excluir_broker
                ) ?? "Sin asignar";
              // Facturación → facturable (descuento de acuerdo, 7.22); Cierres queda en lista (la venta
              // en sí). El total y las filas del DetailsModal leen este importe → quedan coherentes con
              // la celda de Facturación (useVPFacturacionCalcs, también facturable).
              const importeRaw = isCierres ? parseFloat(o.importe) || 0 : getImporteFacturable(o);
              // Resolver mes con misma prioridad
              let mesIdx = -1;
              if (!isCierres && o.facturafecha) {
                const d = new Date(o.facturafecha);
                if (!isNaN(d.getTime())) mesIdx = d.getMonth();
              }
              if (mesIdx < 0 && o[campoMes]) {
                mesIdx = Number(o[campoMes].split("-")[0]) - 1;
              }
              const ultimoDia = new Date(ano, (mesIdx >= 0 ? mesIdx : 0) + 1, 0);
              const fechaConv = ultimoDia.toISOString().split("T")[0];
              const importeLocal = o.moneda_dolar
                ? convertirAMonedaLocal(importeRaw, "USD", pais, fechaConv)
                : importeRaw;
              return {
                ...o,
                esBTL: false,
                esCarteleria: false,
                ejecutivo: ejecutivoNombre,
                cliente: o.cliente || "Sin cliente",
                importe: importeLocal,
                importeOriginalMoneda: importeRaw,
              };
            }),
        ];

        // CONTRATOS (sumados en publicidad)
        if (isCierres) {
          todas = [
            ...todas,
            ...contratosGrupo
              .filter((c) => {
                if (c.estatus !== "100%" || c.eliminada || !c.mes_cierre) return false;
                const [mesStr, anoStr] = c.mes_cierre.split("-");
                return Number(anoStr) === ano && Number(mesStr) >= start && Number(mesStr) <= end;
              })
              .map((c) => {
                const clienteObj = clientes[c.client_id];
                const clienteNombre = clienteObj?.empresa || clienteObj?.nombre || c.cliente || "Sin cliente";
                const ejecutivoId = clienteObj?.ejecutivo ?? c.ejecutivo;
                const ejecutivoNombre =
                  resolverNombreEjecutivo(
                    ejecutivoId,
                    usuarios,
                    intermediarios,
                    c.client_id,
                    clientes,
                    c.created_at,
                    c.estatus,
                    c.fecha_cierre,
                    c.excluir_broker
                  ) ??
                  (c.display_id || "Sin asignar");
                const importeRaw = Number(c.importe_total) || 0;
                const mesIdxC = Number(c.mes_cierre?.split("-")[0]) - 1;
                const ultimoDiaC = new Date(ano, (mesIdxC >= 0 ? mesIdxC : 0) + 1, 0);
                const fechaConvC = ultimoDiaC.toISOString().split("T")[0];
                const importeLocal = c.moneda_dolar
                  ? convertirAMonedaLocal(importeRaw, "USD", pais, fechaConvC)
                  : importeRaw;
                return {
                  ...c,
                  esBTL: false,
                  esCarteleria: false,
                  esHardware: false,
                  esContrato: true,
                  cliente: clienteNombre,
                  ejecutivo: ejecutivoNombre,
                  importe: importeLocal,
                  importeOriginalMoneda: importeRaw,
                };
              }),
          ];
        } else {
          // Facturación: explotar JSONB meses
          contratosGrupo
            .filter((c) => !c.eliminada && c.estatus === "100%")
            .forEach((c) => {
              const clienteObj = clientes[c.client_id];
              const clienteNombre = clienteObj?.empresa || clienteObj?.nombre || c.cliente || "Sin cliente";
              const ejecutivoId = clienteObj?.ejecutivo ?? c.ejecutivo;
              const ejecutivoNombre =
                resolverNombreEjecutivo(
                  ejecutivoId,
                  usuarios,
                  intermediarios,
                  c.client_id,
                  clientes,
                  c.created_at,
                  c.estatus,
                  c.fecha_cierre,
                  c.excluir_broker
                ) ??
                (c.display_id || "Sin asignar");
              (c.meses || []).forEach((mesObj) => {
                let monthIdx = -1;
                let yearVal = -1;
                if (mesObj.facturafecha) {
                  const d = new Date(mesObj.facturafecha);
                  if (!isNaN(d.getTime())) {
                    monthIdx = d.getMonth() + 1;
                    yearVal = d.getFullYear();
                  }
                } else if (mesObj.mes) {
                  const [m, y] = mesObj.mes.split("-");
                  monthIdx = Number(m);
                  yearVal = Number(y);
                }
                if (yearVal === ano && monthIdx >= start && monthIdx <= end) {
                  // Rama facturación (else de isCierres): facturable con descuento a nivel contrato (7.22).
                  const importeRawC = getImporteFacturable({
                    importe: mesObj.importe,
                    descuento_acuerdo_pct: c.descuento_acuerdo_pct,
                  });
                  const ultimoDiaFact = new Date(ano, monthIdx, 0);
                  const fechaConvFact = ultimoDiaFact.toISOString().split("T")[0];
                  const importeLocalC = c.moneda_dolar
                    ? convertirAMonedaLocal(importeRawC, "USD", pais, fechaConvFact)
                    : importeRawC;
                  todas.push({
                    ...c,
                    id: `${c.id}-${mesObj.mes}`,
                    esBTL: false,
                    esCarteleria: false,
                    esHardware: false,
                    esContrato: true,
                    cliente: clienteNombre,
                    ejecutivo: ejecutivoNombre,
                    importe: importeLocalC,
                    importeOriginalMoneda: importeRawC,
                    mes_factura: mesObj.mes,
                  });
                }
              });
            });
        }
      }

      if (vertical === "btl" || vertical === null) {
        todas = [
          ...todas,
          ...salesBTLGrupo
            .filter((b) => {
              if (b.status !== 100 || b.eliminada) return false;
              let mesNum = -1;
              let anoNum = -1;
              if (!isCierres && b.facturafecha) {
                const d = new Date(b.facturafecha);
                if (!isNaN(d.getTime())) {
                  mesNum = d.getMonth() + 1;
                  anoNum = d.getFullYear();
                }
              }
              if (mesNum < 0 && b[campoMes]) {
                const [mesStr, anoStr] = b[campoMes].split("-");
                mesNum = Number(mesStr);
                anoNum = Number(anoStr);
              }
              return anoNum === ano && mesNum >= start && mesNum <= end;
            })
            .map((b) => {
              const invRaw = Number(b.total_investment) || 0;
              let mesIdxBTL = -1;
              if (!isCierres && b.facturafecha) {
                const d = new Date(b.facturafecha);
                if (!isNaN(d.getTime())) mesIdxBTL = d.getMonth();
              }
              if (mesIdxBTL < 0 && b[campoMes]) {
                mesIdxBTL = Number(b[campoMes].split("-")[0]) - 1;
              }
              const ultimoDiaBTL = new Date(ano, (mesIdxBTL >= 0 ? mesIdxBTL : 0) + 1, 0);
              const fechaConvBTL = ultimoDiaBTL.toISOString().split("T")[0];
              const invLocal = b.moneda_dolar ? convertirAMonedaLocal(invRaw, "USD", pais, fechaConvBTL) : invRaw;
              return {
                ...b,
                esBTL: true,
                esCarteleria: false,
                esHardware: false,
                cliente: b.cliente || "Sin cliente",
                total_investment: invLocal,
                importeOriginalMoneda: invRaw,
              };
            }),
        ];
      }

      // HARDWARE
      if (vertical === "hardware" || vertical === null) {
        const hardwareItems = negociosCarteleriaGrupo
          .filter((n) => {
            if (!n.incluye_hardware || n.eliminada) return false;
            if (!["100%", "Activo"].includes(n.estado)) return false;
            return true;
          })
          .map((n) => {
            // Cascada: edificios → clientes → fallback (igual que CarteleriaList)
            let nombreReferencia = `ID ${n.referencia_id || n.id}`;
            if (n.referencia_id && edificios[n.referencia_id]) {
              nombreReferencia = edificios[n.referencia_id]?.edificio;
            } else if (n.referencia_id && clientes[n.referencia_id]) {
              nombreReferencia = clientes[n.referencia_id]?.empresa || clientes[n.referencia_id]?.nombre;
            }

            const monedaHW = (n.moneda_hardware || "UF").toUpperCase();
            const esUF = monedaHW.includes("UF");

            let importeMesOriginal = 0;
            let importeMesConvertido = 0;
            let incluyeEnMes = false;
            const ultimoDiaMes = new Date(ano, end, 0);
            const fechaConversion = ultimoDiaMes.toISOString().split("T")[0];

            if (isCierres) {
              const fecha = new Date(n.fecha_cierre);
              if (!isNaN(fecha)) {
                const mesNegocio = fecha.getMonth() + 1;
                const anoNegocio = fecha.getFullYear();
                if (anoNegocio === ano && mesNegocio >= start && mesNegocio <= end) {
                  incluyeEnMes = true;
                  importeMesOriginal = Number(n.monto_hardware) || 0;
                  importeMesConvertido = convertirAMonedaLocal(importeMesOriginal, monedaHW, pais, fechaConversion);
                }
              }
            } else {
              // Detalle del modal Hardware: MISMO criterio que el agregado (calcularHardwareMensual).
              // Mes de FACTURACIÓN del tramo (devengo, 10.143), cascada: fecha real (`facturafecha` ||
              // `fecha_factura`) → `fecha_factura_estimada` del tramo pendiente → `fecha_cierre` (fallback
              // tramos viejos). `fecha_cobro_estimada` NO participa (es dato de cobro/Caja).
              const tieneDesglose =
                n.modalidad === "CAPEX" &&
                Array.isArray(n.detalle_pagos_hardware) &&
                n.detalle_pagos_hardware.length > 0;

              if (tieneDesglose) {
                const pagosMes = n.detalle_pagos_hardware.filter((pago) => {
                  const fechaTramo =
                    pago.facturafecha || pago.fecha_factura || pago.fecha_factura_estimada || n.fecha_cierre;
                  if (!fechaTramo || pago.monto == null) return false;
                  const fecha = new Date(fechaTramo);
                  const mes = fecha.getMonth() + 1;
                  const añoPago = fecha.getFullYear();
                  return añoPago === ano && mes >= start && mes <= end;
                });
                if (pagosMes.length > 0) {
                  incluyeEnMes = true;
                  importeMesOriginal = pagosMes.reduce((sum, pago) => sum + (Number(pago.monto) || 0), 0);
                  importeMesConvertido = pagosMes.reduce((sum, pago) => {
                    const fechaPago = (pago.facturafecha || pago.fecha_factura)?.split("T")[0] || fechaConversion;
                    return sum + convertirAMonedaLocal(Number(pago.monto) || 0, monedaHW, pais, fechaPago);
                  }, 0);
                }
              } else if (n.modalidad === "CAPEX" && n.fecha_cierre) {
                const fecha = new Date(n.fecha_cierre);
                const mes = fecha.getMonth() + 1;
                const añoFecha = fecha.getFullYear();
                if (añoFecha === ano && mes >= start && mes <= end) {
                  incluyeEnMes = true;
                  importeMesOriginal = Number(n.monto_hardware) || 0;
                  importeMesConvertido = convertirAMonedaLocal(importeMesOriginal, monedaHW, pais, fechaConversion);
                }
              } else if (n.modalidad === "OPEX" && n.cuota_hardware_opex && n.fecha_cierre) {
                const fechaCierre = new Date(n.fecha_cierre);
                const mesCierre = fechaCierre.getMonth();
                const añoCierre = fechaCierre.getFullYear();
                const totalCuotas = Number(n.num_cuotas) || Number(n.plazo_contrato_meses) || 36;

                for (let i = 0; i < totalCuotas; i++) {
                  const mesTotal = mesCierre + i;
                  const añoActual = añoCierre + Math.floor(mesTotal / 12);
                  const mesActual = mesTotal % 12;
                  if (añoActual === ano && mesActual + 1 >= start && mesActual + 1 <= end) {
                    incluyeEnMes = true;
                    const pantallas = Number(n.cantidad_pantallas) || 1;
                    importeMesOriginal = Number(n.cuota_hardware_opex) * pantallas;
                    importeMesConvertido = convertirAMonedaLocal(importeMesOriginal, monedaHW, pais, fechaConversion);
                    break;
                  }
                }
              }
            }

            return {
              ...n,
              esBTL: false,
              esCarteleria: false,
              esHardware: true,
              cliente: nombreReferencia,
              importe: importeMesConvertido,
              importeOriginal: importeMesOriginal,
              monedaOriginal: monedaHW,
              esMonedaUF: esUF,
              incluyeEnMes,
            };
          })
          .filter((item) => item.incluyeEnMes);

        todas = [...todas, ...hardwareItems];
      }

      // CARTELERÍA
      if (vertical === "carteleria" || vertical === null) {
        const resolverNombreReferencia = (n) => {
          if (n.referencia_id && edificios[n.referencia_id]) {
            return edificios[n.referencia_id]?.edificio;
          }
          if (n.referencia_id && clientes[n.referencia_id]) {
            return clientes[n.referencia_id]?.empresa || clientes[n.referencia_id]?.nombre;
          }
          return `ID ${n.referencia_id || n.id}`;
        };

        if (isCierres) {
          const ultimoDiaMes = new Date(ano, end, 0);
          const fechaConversion = ultimoDiaMes.toISOString().split("T")[0];

          negociosCarteleriaGrupo.forEach((n) => {
            if (n.eliminada) return;
            if (!["100%", "Activo"].includes(n.estado)) return;

            const pantallas = parseInt(n.cantidad_pantallas) || 1;
            const plazo = parseInt(n.plazo_contrato_meses) || 0;
            const gestion = parseFloat(n.fee_gestion_unitario) || 0;
            const contenidosUnit = parseFloat(n.fee_contenidos_unitario) || 0;
            const tieneContenidosRangoPropio = Boolean(n.incluye_contenidos && n.contenidos_fecha_inicio);
            const contenidosEnBase = n.incluye_contenidos && !tieneContenidosRangoPropio ? contenidosUnit : 0;

            const monedaGestion = (n.moneda_gestion || "UF").toUpperCase();
            const monedaContenidos = (n.moneda_contenidos || "UF").toUpperCase();
            const esUF = monedaGestion.includes("UF") || monedaContenidos.includes("UF");
            const nombreReferencia = resolverNombreReferencia(n);

            // Cierre base (gestión + contenidos si no hay rango propio)
            if (n.fecha_cierre) {
              const fecha = new Date(n.fecha_cierre + "T00:00:00");
              if (!isNaN(fecha)) {
                const mesNegocio = fecha.getMonth() + 1;
                if (fecha.getFullYear() === ano && mesNegocio >= start && mesNegocio <= end) {
                  const gestionLocal = convertirAMonedaLocal(gestion * pantallas, monedaGestion, pais, fechaConversion);
                  const contenidosLocal = convertirAMonedaLocal(
                    contenidosEnBase * pantallas,
                    monedaContenidos,
                    pais,
                    fechaConversion
                  );
                  const montoMensualOriginal = (gestion + contenidosEnBase) * pantallas;
                  const montoTotalOriginal = montoMensualOriginal * plazo;
                  const montoTotalCLP = (gestionLocal + contenidosLocal) * plazo;

                  todas.push({
                    ...n,
                    esBTL: false,
                    esCarteleria: true,
                    esHardware: false,
                    cliente: nombreReferencia,
                    importe: montoTotalCLP,
                    importeOriginal: montoTotalOriginal,
                    monedaOriginal: monedaGestion,
                    esMonedaUF: esUF,
                    pantallas,
                    plazo,
                    feeGestion: gestion,
                    feeContenidos: contenidosEnBase,
                  });
                }
              }
            }

            // Cierre adicional de contenidos con rango propio
            if (tieneContenidosRangoPropio) {
              const fechaCont = new Date(n.contenidos_fecha_inicio + "T00:00:00");
              if (!isNaN(fechaCont)) {
                const mesCont = fechaCont.getMonth() + 1;
                if (fechaCont.getFullYear() === ano && mesCont >= start && mesCont <= end) {
                  const fechaFinCont = n.contenidos_fecha_fin ? new Date(n.contenidos_fecha_fin + "T00:00:00") : null;
                  const plazoCont = fechaFinCont
                    ? (fechaFinCont.getFullYear() - fechaCont.getFullYear()) * 12 +
                      (fechaFinCont.getMonth() - fechaCont.getMonth()) +
                      1
                    : parseInt(n.plazo_contrato_meses) || 0;

                  const contenidosLocal = convertirAMonedaLocal(
                    contenidosUnit * pantallas,
                    monedaContenidos,
                    pais,
                    fechaConversion
                  );
                  const montoMensualOriginal = contenidosUnit * pantallas;
                  const montoTotalOriginal = montoMensualOriginal * plazoCont;
                  const montoTotalCLP = contenidosLocal * plazoCont;

                  todas.push({
                    ...n,
                    id: `${n.id}-contenidos`,
                    esBTL: false,
                    esCarteleria: true,
                    esHardware: false,
                    cliente: `${nombreReferencia} (contenidos)`,
                    importe: montoTotalCLP,
                    importeOriginal: montoTotalOriginal,
                    monedaOriginal: monedaContenidos,
                    esMonedaUF: monedaContenidos.includes("UF"),
                    pantallas,
                    plazo: plazoCont,
                    feeGestion: 0,
                    feeContenidos: contenidosUnit,
                  });
                }
              }
            }
          });
        } else {
          todas = [
            ...todas,
            ...negociosCarteleriaGrupo
              .filter((n) => {
                if (n.eliminada) return false;
                if (!["100%", "Activo"].includes(n.estado) || !n.fecha_inicio) return false;
                const fechaInicio = new Date(n.fecha_inicio);
                const plazoMeses = parseInt(n.plazo_contrato_meses) || 0;
                if (plazoMeses === 0) return false;
                const fechaFin = new Date(fechaInicio);
                fechaFin.setMonth(fechaFin.getMonth() + plazoMeses);
                const ultimoDiaPeriodo = new Date(ano, end, 0);
                const primerDiaPeriodo = new Date(ano, start - 1, 1);
                return fechaInicio <= ultimoDiaPeriodo && primerDiaPeriodo < fechaFin;
              })
              .map((n) => {
                const pantallas = parseInt(n.cantidad_pantallas) || 1;
                const plazo = parseInt(n.plazo_contrato_meses) || 0;
                const gestion = parseFloat(n.fee_gestion_unitario) || 0;
                const contenidos = n.incluye_contenidos ? parseFloat(n.fee_contenidos_unitario) || 0 : 0;

                const monedaGestion = (n.moneda_gestion || "UF").toUpperCase();
                const monedaContenidos = (n.moneda_contenidos || "UF").toUpperCase();
                const esUF = monedaGestion.includes("UF") || monedaContenidos.includes("UF");

                const montoMensualOriginal = (gestion + contenidos) * pantallas;

                const ultimoDiaMes = new Date(ano, end, 0);
                const fechaConversion = ultimoDiaMes.toISOString().split("T")[0];

                const gestionCLP = convertirAMonedaLocal(gestion * pantallas, monedaGestion, pais, fechaConversion);
                const contenidosCLP = convertirAMonedaLocal(
                  contenidos * pantallas,
                  monedaContenidos,
                  pais,
                  fechaConversion
                );
                const montoMensualCLP = gestionCLP + contenidosCLP;

                return {
                  ...n,
                  esBTL: false,
                  esCarteleria: true,
                  esHardware: false,
                  cliente: resolverNombreReferencia(n),
                  importe: montoMensualCLP,
                  importeOriginal: montoMensualOriginal,
                  monedaOriginal: monedaGestion,
                  esMonedaUF: esUF,
                  pantallas,
                  plazo,
                  feeGestion: gestion,
                  feeContenidos: contenidos,
                };
              }),
          ];
        }
      }

      const isFacturacion = tipo === "facturacion";
      setModal({
        open: true,
        title:
          `${isFacturacion ? "Facturación" : "Cierres"}` +
          `${vertical ? ` - ${vertical.charAt(0).toUpperCase() + vertical.slice(1)}` : ""}` +
          ` - ${getMonthName(monthIndex)} ${ano}`,
        items: todas.sort((a, b) => (b.importe || b.total_investment || 0) - (a.importe || a.total_investment || 0)),
      });
    },
    [
      oportunidadesGrupo,
      salesBTLGrupo,
      negociosCarteleriaGrupo,
      contratosGrupo,
      usuarios,
      intermediarios,
      clientes,
      edificios,
      pais,
      currentYear,
      getMonthName,
    ]
  );

  const handleOpenPipelineModal = useCallback(
    (monthIndex, isTotal = false) => {
      const items = getPipelineItems(monthIndex, isTotal);
      const mesNombre = isTotal ? `TOTAL ${anoSeleccionado}` : getMonthName(monthIndex);

      let total = 0;
      items.forEach((op) => {
        const importe = parseFloat(op.importe) || 0;
        const factor = op.estatus === "90%" ? FACTOR_90 : FACTOR_75;
        const importeAjustado = importe * factor;
        if (showInDollars || modoCombinado) {
          const rateOp = op.paisOrigen === pais ? tipoCambioActual : tipoCambioOtroPais || tipoCambioActual;
          total += importeAjustado / rateOp;
        } else {
          total += importeAjustado;
        }
      });

      setPipelineModal({
        open: true,
        title: `Pipeline - ${mesNombre}`,
        items,
        total,
      });
    },
    [
      getPipelineItems,
      getMonthName,
      showInDollars,
      modoCombinado,
      tipoCambioActual,
      tipoCambioOtroPais,
      pais,
      anoSeleccionado,
    ]
  );

  // === RENDER ===

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ventas-proyeccion-wrapper">
      <VPHeader
        vistaCompleta={vistaCompleta}
        setVistaCompleta={setVistaCompleta}
        mostrar2025={mostrar2025}
        setMostrar2025={setMostrar2025}
        showInDollars={showInDollars}
        setShowInDollars={setShowInDollars}
        verChileYPeru={verChileYPeru}
        setVerChileYPeru={setVerChileYPeru}
        verYTD={verYTD}
        setVerYTD={setVerYTD}
        filtroGrupo={filtroGrupo}
        setFiltroGrupo={setFiltroGrupo}
        pais={pais}
        logo={verChileYPeru ? regionLogo : undefined}
        anoSeleccionado={anoSeleccionado}
        setAnoSeleccionado={setAnoSeleccionado}
        aniosDisponibles={aniosDisponibles}
        currentYear={currentYear}
        getDesglose={getDesglose}
        getMoneda={getMoneda}
        getDisplayValue={getDisplayValue}
        getMonthName={getMonthName}
        currentMonthIndex={currentMonthIndex}
        calcularPipeline={calcularPipeline}
        calcularPipelineFacturacion={calcularPipelineFacturacion}
        onOpenChart={() => setChartModalOpen(true)}
      />

      <div className="ventas-proyeccion">
        <div className={styles.tableWrapper}>
          <VentasTable
            vistaCompleta={vistaCompleta}
            mostrar2025={mostrar2025}
            verYTD={verYTD}
            anoSel={anoSeleccionado}
            anoAA={anoAA}
            anoAAA={anoAAA}
            getDesglose={getDesglose}
            getMoneda={getMoneda}
            getDisplayValue={getDisplayValue}
            getMonthName={getMonthName}
            calcularVariacion={calcularVariacion}
            shouldShowVsAA={shouldShowVsAA}
            calcularPipelineBruto={calcularPipelineBruto}
            calcularPipelineBrutoTotal={calcularPipelineBrutoTotal}
            onOpenModal={handleOpenModal}
            onOpenPipelineModal={handleOpenPipelineModal}
            currentMonthIndex={currentMonthIndex}
            styles={styles}
          />

          <DetailsModal
            modal={modal}
            onClose={() => setModal({ open: false, title: "", items: [] })}
            showInDollars={showInDollars}
            getMoneda={getMoneda}
            localString={localString}
            pais={pais}
            tipoCambioActual={tipoCambioActual}
            styles={styles}
          />

          <PipelineModal
            pipelineModal={pipelineModal}
            onClose={() => setPipelineModal({ open: false, title: "", items: [], total: 0 })}
            pipelineSort={pipelineSort}
            setPipelineSort={setPipelineSort}
            showInDollars={showInDollars}
            modoCombinado={modoCombinado}
            clientes={clientes}
            moneda={moneda}
            localString={localString}
            tipoCambioActual={tipoCambioActual}
            pais={pais}
            styles={styles}
          />

          <ChartModal
            open={chartModalOpen}
            onClose={() => setChartModalOpen(false)}
            getDesglose={getDesglose}
            getMoneda={getMoneda}
            getDisplayValue={getDisplayValue}
            currentMonthIndex={currentMonthIndex}
            anoSel={anoSeleccionado}
            anoAA={anoAA}
            currentYear={currentYear}
            pais={pais}
            styles={styles}
            oportunidades={oportunidades}
            contratosData={contratosData}
            showInDollars={showInDollars}
          />
        </div>
      </div>
    </div>
  );
};

export default VentasProyeccion;
